// src/context/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// Create the context
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // Verify token with backend
  const verifyToken = async (token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:31000'}/auth/verify-token`,
        { token },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      return response.data.valid;
    } catch (error) {
      console.error('Token verification error:', error);
      return false;
    }
  };

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        const storedRole = localStorage.getItem('userRole');

        if (storedUser && storedToken && storedRole) {
          // Verify token with backend
          const isValid = await verifyToken(storedToken);
          
          if (isValid) {
            setUser(JSON.parse(storedUser));
            setIsAuthenticated(true);
          } else {
            // Token invalid, clear storage
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            localStorage.removeItem('userRole');
          }
        }
      } catch (error) {
        console.error('Auth check error:', error);
        // Clear invalid data
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);


  // Login function
  const login = async (email, password, role, token, userData) => {
    try {
      setLoading(true);
      
      // If token and userData are provided (from API), use them
      if (token && userData) {
        // Store in localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', token);
        localStorage.setItem('userRole', role);

        // Update state
        setUser(userData);
        setIsAuthenticated(true);

        return { success: true, user: userData };
      }
      
      // Store in localStorage
      localStorage.setItem('user', null);
      localStorage.setItem('token', null);
      localStorage.setItem('userRole', role);

      // Update state
      setUser(null);
      setIsAuthenticated(true);

      return { success: false, error:"UserData not available",  user: null };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Switch dashboard function
  const switchDashboard = (targetRole) => {
    if (!user) return false;
    
    // Check if user has permission to access the target dashboard
    const hasPermission = checkDashboardAccess(targetRole);
    
    if (hasPermission) {
      // Store current role in session storage (optional, for analytics)
      sessionStorage.setItem('previousRole', user.role);
      
      // Update user role in state (but keep the same user data)
      const updatedUser = { ...user, currentDashboard: targetRole };
      setUser(updatedUser);
      
      // Navigate to the appropriate dashboard
      switch (targetRole) {
        case 'admin':
          navigate('/editors/dashboard');
          break;
        case 'reviewer':
          navigate('/reviewerdash');
          break;
        case 'author':
          navigate('/dashboard');
          break;
        default:
          return false;
      }
      return true;
    }
    return false;
  };

  // Check if user has access to a specific dashboard
  const checkDashboardAccess = (targetRole) => {
    if (!user) return false;
    
    switch (targetRole) {
      case 'admin':
        return user.canAccessEditor === true;
      case 'reviewer':
        return user.canAccessReviewer === true;
      case 'author':
        return user.canAccessAuthor === true;
      default:
        return false;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (token) {
        // Call backend to invalidate session
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000/api'}/auth/logout`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        ).catch(err => console.error('Logout API error:', err));
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear localStorage regardless of API response
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('userRole');
      
      // Clear state
      setUser(null);
      setIsAuthenticated(false);
      
      // Navigate to home
      navigate('/');
    }
  };

  // Update user profile
  const updateUser = (userData) => {
    const updatedUser = { ...user, ...userData };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  // Check if user has required role
  const hasRole = (requiredRole) => {
    if (!user) return false;
    if (requiredRole === 'any') return true;
    return user.role === requiredRole || user.currentDashboard === requiredRole;
  };

  // Get available dashboards for current user
  const getAvailableDashboards = () => {
    if (!user) return [];
    
    const dashboards = [];
    if (user.canAccessAuthor) dashboards.push({ role: 'author', label: 'Author Dashboard', path: '/dashboard' });
    if (user.canAccessReviewer) dashboards.push({ role: 'reviewer', label: 'Reviewer Dashboard', path: '/reviewerdash' });
    if (user.canAccessEditor) dashboards.push({ role: 'admin', label: 'Editor Dashboard', path: '/editors/dashboard' });
    
    return dashboards;
  };

  // Value object to be provided
  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    hasRole,
    switchDashboard,
    checkDashboardAccess,
    getAvailableDashboards
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;